import { useMemo, useState } from "react";
import {
  Box,
  Heading,
  Text,
  Container,
  VStack,
  Input,
  Button,
  HStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Alert,
  AlertIcon,
  Link,
} from "@chakra-ui/react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { login } from "../api/auth";
import { useAuth } from "../auth/AuthContext";

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  const { loginWithSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const redirectTo = useMemo(
    () => searchParams.get("redirect") || location.state?.from || "/",
    [searchParams, location.state]
  );
  const sessionExpired = searchParams.get("expired") === "1";

  const emailTrim = email.trim();

  const emailError = !emailTrim
    ? "Email is required."
    : !isValidEmail(emailTrim)
      ? "Please enter a valid email address."
      : "";

  const passwordError = !password ? "Password is required." : "";

  async function onSubmit(e) {
    e.preventDefault();
    setApiError("");

    if (emailError || passwordError) return;

    try {
      setSubmitting(true);

      const session = await login({
        email: emailTrim,
        password,
      });

      loginWithSession(session);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Login failed.";

      setApiError(typeof msg === "string" ? msg : "Login failed.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <style>{`
        .poolBackground {
          background: linear-gradient(
            120deg,
            #0a3d62,
            #1e81b0,
            #3caea3,
            #1e81b0,
            #0a3d62
          );
          background-size: 400% 400%;
          animation: poolShimmer 8s ease infinite;
        }

        @keyframes poolShimmer {
          0% {
            background-position: 0% 50%;
            filter: brightness(100%);
          }
          50% {
            background-position: 100% 50%;
            filter: brightness(120%);
          }
          100% {
            background-position: 0% 50%;
            filter: brightness(100%);
          }
        }
      `}</style>

      <Box minH="100vh" className="poolBackground" color="white">
        <Box bg="rgba(0, 0, 40, 0.4)" minH="100vh" px={4} py={20}>
          <Container maxW="container.md" centerContent>
            <VStack spacing={8} align="stretch">
              <Box
                bg="#00007f"
                px={8}
                py={6}
                borderRadius="md"
                boxShadow="lg"
                textAlign="center"
              >
                <HStack justify="center" align="center" spacing={4}>
                  <Heading
                    fontFamily="'Orbitron', sans-serif"
                    fontSize="48px"
                    letterSpacing="2px"
                    lineHeight="1"
                  >
                    THE DEEP END
                  </Heading>
                </HStack>
              </Box>

              <Box textAlign="center">
                <Text fontSize="xl">Boards are closed. Join us in the pool!</Text>
              </Box>

              <Box
                as="form"
                onSubmit={onSubmit}
                bg="rgba(255, 255, 255, 0.15)"
                px={6}
                py={8}
                borderRadius="md"
                boxShadow="xl"
                backdropFilter="blur(6px)"
              >
                <VStack spacing={4}>
                  {sessionExpired && (
                    <Alert status="error" borderRadius="md">
                      <AlertIcon />
                      Your session has expired. Please log in again.
                    </Alert>
                  )}
                  {apiError ? (
                    <Alert status="error" borderRadius="md">
                      <AlertIcon />
                      {apiError}
                    </Alert>
                  ) : null}

                  <FormControl isInvalid={!!emailError && email.length > 0}>
                    <FormLabel>Email</FormLabel>
                    <Input
                      placeholder="Email"
                      type="email"
                      variant="filled"
                      bg="white"
                      color="black"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="username"
                    />
                    <FormErrorMessage>{emailError}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!passwordError && password.length > 0}>
                    <FormLabel>Password</FormLabel>
                    <Input
                      placeholder="Password"
                      type="password"
                      variant="filled"
                      bg="white"
                      color="black"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                    />
                    <FormErrorMessage>{passwordError}</FormErrorMessage>
                  </FormControl>

                  <HStack w="100%" justify="flex-end">
                    <Link
                      as={RouterLink}
                      to="/forgot-password"
                      color="orange.200"
                      fontSize="sm"
                    >
                      Forgot Password?
                    </Link>
                  </HStack>

                  <Button
                    type="submit"
                    bg="#00007f"
                    color="white"
                    w="100%"
                    isLoading={submitting}
                    loadingText="Logging in"
                  >
                    Login
                  </Button>

                  <HStack justify="space-between" w="100%">
                    <Text fontSize="sm" opacity={0.9}>
                      New here?
                    </Text>
                    <Link
                      as={RouterLink}
                      to="/signup"
                      color="orange.200"
                      fontWeight="600"
                    >
                      Create an account
                    </Link>
                  </HStack>
                </VStack>
              </Box>
            </VStack>
          </Container>
        </Box>
      </Box>
    </>
  );
}